import { useEffect, useState } from 'react';

import { getPatientStatusService } from '../services';
import type { PatientUnavailableReason } from '../pages/hospital/patient/PatientUnavailableNotice';

interface PatientUnavailableState {
    loading: boolean;
    reason: PatientUnavailableReason | null;
    deletedAt?: string;
}

const NOT_ASKED: PatientUnavailableState = { loading: false, reason: null, deletedAt: undefined };
const ASKING: PatientUnavailableState = { loading: true, reason: null, deletedAt: undefined };

/**
 * Why the patient in the URL is not in the investigation's local list (SHER-258).
 *
 * The patient page used to answer every miss with "you don't have access to this patient",
 * including for a patient another user had simply deleted. Only when `patientMissing` is true
 * does this ask the API; until it answers the caller should show a loader, not an error.
 * Any failure falls back to the neutral `not-found`, which never claims an access problem.
 */
export function usePatientUnavailableReason(uuidInvestigation: string, uuidPatient: string, patientMissing: boolean): PatientUnavailableState {
    // Keyed by the patient asked about, so a uuid change never shows the previous answer.
    const key = `${uuidInvestigation}/${uuidPatient}`;
    const [answer, setAnswer] = useState<{ key: string; state: PatientUnavailableState } | null>(null);

    useEffect(() => {
        if (!patientMissing) {
            return;
        }
        let cancelled = false;
        const setState = (state: PatientUnavailableState) => setAnswer({ key, state });
        getPatientStatusService(uuidInvestigation, uuidPatient)
            .then((response: { patientStatus?: string; deletedAt?: string }) => {
                if (cancelled) return;
                if (response.patientStatus === 'deleted') {
                    setState({ loading: false, reason: 'deleted', deletedAt: response.deletedAt });
                } else if (response.patientStatus === 'active') {
                    setState({ loading: false, reason: 'not-loaded', deletedAt: undefined });
                } else {
                    setState({ loading: false, reason: 'not-found', deletedAt: undefined });
                }
            })
            .catch(() => {
                if (!cancelled) setState({ loading: false, reason: 'not-found', deletedAt: undefined });
            });
        return () => { cancelled = true; };
    }, [key, uuidInvestigation, uuidPatient, patientMissing]);

    if (!patientMissing) {
        return NOT_ASKED;
    }
    return answer && answer.key === key ? answer.state : ASKING;
}
